import { MigrationManifest } from 'redux-persist';

/**
 * Собирает миграцию «переименовать литерал вида в `listPreferences.viewModes`».
 * Таких миграций уже две (`4`: `grid` → `card`, `5`: `large` → `card`, обе
 * 18976), и отличаются они ровно парой литералов — сам приём один: слайс,
 * которого не было, не создаём; чужие значения не трогаем; переписываем только
 * совпавший литерал.
 *
 * Билдер НЕ склеивает миграции в одну: каждая остаётся отдельной записью
 * манифеста со СВОИМ номером версии и своей замкнутой парой литералов, и гейт
 * («состояние вообще персистилось» + «слайс в нём есть») выполняется в каждой
 * из них независимо — redux-persist прогоняет их по очереди, и пропуск любой
 * из версий оставил бы в сторе несуществующий литерал вида.
 * @param from - прежний литерал вида, который надо переписать
 * @param to - литерал, на который он заменяется
 */
const buildViewModeRename =
  (from: string, to: string): MigrationManifest[string] =>
  /**
   * @param state - Состояние
   */
  state => {
    const listPreferences = (
      state as { listPreferences?: { viewModes?: Record<string, string> } }
    )?.listPreferences;

    if (!state?._persist || !listPreferences) {
      return state;
    }

    return {
      ...state,
      listPreferences: {
        ...listPreferences,
        viewModes: Object.fromEntries(
          Object.entries(listPreferences.viewModes ?? {}).map(
            ([listContext, mode]) => [listContext, mode === from ? to : mode]
          )
        )
      }
    };
  };

export const migrations: MigrationManifest = {
  /**
   * Сброс слайса videoReadingSettings
   * @param state Состояние
   */
  1: state => {
    return state?._persist
      ? {
          ...state,
          videoReadingSettings: undefined
        }
      : state;
  },

  /**
   * Добавление пустого слайса listPreferences (память режимов отображения карточек)
   * @param state Состояние
   */
  2: state => {
    return state?._persist
      ? {
          ...state,
          listPreferences: { viewModes: {} }
        }
      : state;
  },

  /**
   * Добавление поля tableColumns в слайс listPreferences (набор столбцов таблицы
   * реестра визиток, 18699). Слайс приезжает из storage целиком (autoMergeLevel1
   * делает hard-set ключа), поэтому без миграции поле осталось бы undefined у всех
   * существующих пользователей; запомненные виды при этом сохраняются.
   * @param state Состояние
   */
  3: state => {
    return state?._persist
      ? {
          ...state,
          listPreferences: {
            ...(state as { listPreferences?: object }).listPreferences,
            tableColumns: {}
          }
        }
      : state;
  },

  /**
   * Переименование литерала вида `grid` в `card` в `listPreferences.viewModes`
   * (18976: единый словарь видов отображения списков — `grid` и `card` означали
   * одно и то же в разных доменах, теперь один литерал `card`). Чистое
   * переименование значения, а не добавление поля: остальные значения не трогает,
   * а слайс, которого не было, не создаёт (в отличие от миграций `2` и `3`, которые
   * ДОБАВЛЯЮТ отсутствующую форму) — см. `buildViewModeRename`.
   */
  4: buildViewModeRename('grid', 'card'),

  /**
   * Переименование литерала вида `large` в `card` в `listPreferences.viewModes`
   * (18976: «Крупно» убирается из словаря видов — решение заказчика, см.
   * `docs/superpowers/specs/2026-09-16-18976-design-pass.md`, Р7). Отдельная
   * версия, а не дополнение миграции `4`: пользователь, чей стор уже прошёл
   * версию 4, к этому переименованию не возвращался бы вовсе.
   */
  5: buildViewModeRename('large', 'card')
};
